import React from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { useForm, ValidationError } from "@formspree/react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const FORMSPREE_ID = "xvzewero";

const Contact = () => {
  const [state, handleSubmit] = useForm(FORMSPREE_ID);

  return (
    <section id="contact" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-2">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#b22234] font-bold mb-4">
              Contact
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-extrabold text-[#0a3161] leading-tight">
              Get In Touch
            </h2>
            <div className="mt-6 h-1 w-20 bg-[#b22234] rounded-full" />
            <p className="mt-8 text-slate-600 text-[16px] leading-relaxed">
              Feel free to reach out or fill out the form below for any inquiries.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href="mailto:gregclarkfor19thdistrict@gmail.com"
                className="lift flex items-center gap-4 p-4 rounded-xl border border-slate-200 bg-white hover:border-[#0a3161]"
              >
                <div className="w-11 h-11 rounded-lg bg-[#b22234]/10 flex items-center justify-center text-[#b22234]">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</div>
                  <div className="text-[#0a3161] font-semibold">gregclarkfor19thdistrict@gmail.com</div>
                </div>
              </a>
              <a
                href="tel:+13152365975"
                className="lift flex items-center gap-4 p-4 rounded-xl border border-slate-200 bg-white hover:border-[#0a3161]"
              >
                <div className="w-11 h-11 rounded-lg bg-[#0a3161]/10 flex items-center justify-center text-[#0a3161]">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Phone</div>
                  <div className="text-[#0a3161] font-semibold">(315) 236-5975</div>
                </div>
              </a>
              <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 bg-slate-50">
                <div className="w-11 h-11 rounded-lg bg-white flex items-center justify-center text-[#0a3161] border border-slate-200">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">District</div>
                  <div className="text-[#0a3161] font-semibold">19th District, Oswego County</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            {state.succeeded ? (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-10 md:p-14 shadow-sm text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="mt-6 font-serif-display text-3xl font-extrabold text-[#0a3161]">
                  Message Sent!
                </h3>
                <p className="mt-3 text-slate-600 max-w-md mx-auto">
                  Thank you for reaching out. Greg will be in touch as soon as possible.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-slate-50 border border-slate-200 rounded-2xl p-8 md:p-10 shadow-sm"
              >
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="text-sm font-semibold text-[#0a3161]">
                      Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      required
                      placeholder="Your name"
                      className="mt-2 h-12 border-slate-300 bg-white focus-visible:ring-[#0a3161] focus-visible:border-[#0a3161]"
                    />
                    <ValidationError
                      prefix="Name"
                      field="name"
                      errors={state.errors}
                      className="mt-1 text-xs text-red-600"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="text-sm font-semibold text-[#0a3161]">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="mt-2 h-12 border-slate-300 bg-white focus-visible:ring-[#0a3161] focus-visible:border-[#0a3161]"
                    />
                    <ValidationError
                      prefix="Email"
                      field="email"
                      errors={state.errors}
                      className="mt-1 text-xs text-red-600"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="phone" className="text-sm font-semibold text-[#0a3161]">
                      Phone
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      placeholder="(315) 555-0100"
                      className="mt-2 h-12 border-slate-300 bg-white focus-visible:ring-[#0a3161] focus-visible:border-[#0a3161]"
                    />
                    <ValidationError
                      prefix="Phone"
                      field="phone"
                      errors={state.errors}
                      className="mt-1 text-xs text-red-600"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="message" className="text-sm font-semibold text-[#0a3161]">
                      Message
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={5}
                      required
                      placeholder="How can we help?"
                      className="mt-2 border-slate-300 bg-white focus-visible:ring-[#0a3161] focus-visible:border-[#0a3161]"
                    />
                    <ValidationError
                      prefix="Message"
                      field="message"
                      errors={state.errors}
                      className="mt-1 text-xs text-red-600"
                    />
                  </div>
                </div>

                {/* Honeypot for spam protection */}
                <input type="text" name="_gotcha" style={{ display: "none" }} tabIndex="-1" autoComplete="off" />

                <ValidationError
                  errors={state.errors}
                  className="mt-4 text-sm text-red-600"
                />

                <Button
                  type="submit"
                  disabled={state.submitting}
                  className="mt-6 bg-[#0a3161] hover:bg-[#082448] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-full h-12 px-8 text-[15px] font-oswald font-semibold uppercase tracking-[0.14em]"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {state.submitting ? "Sending..." : "Submit"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
